import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, MessageCircle, Navigation, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    subject: '',
    advisoryType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const serviceID = 'service_z0ho1ss';
    const templateID = 'template_ggsqtfq';
    const publicKey = 'oG7EbqN1Po7kZ8kxe';

    const templateParams = {
      from_name: formData.name,
      from_mobile: formData.mobile,
      from_email: formData.email || 'Not provided',
      subject: formData.subject,
      advisory_type: formData.advisoryType,
      message: formData.message,
      to_name: 'Consultancy Firm Team',
    };

    try {
      const response = await emailjs.send(
        serviceID,
        templateID,
        templateParams,
        publicKey
      );

      console.log('Email sent successfully!', response.status, response.text);
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your inquiry has been submitted successfully. We will get back to you within 24 hours.'
      });

      setFormData({
        name: '',
        mobile: '',
        email: '',
        subject: '',
        advisoryType: '',
        message: ''
      });

    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Oops! Something went wrong. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const phoneNumber = '919217468906';
    const message = 'Hello! I would like to inquire about your consultancy services.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleOpenMap = () => {
    const mapUrl = 'https://www.google.com/maps/search/?api=1&query=Gold+Tower+Wave+One+Sector+18+Noida';
    window.open(mapUrl, '_blank', 'noopener,noreferrer');
  };

  const advisoryTypes = [
    'Business Strategy',
    'Risk & Compliance',
    'Financial Advisory',
    'Operational Excellence',
    'Regulatory Consulting',
    'Digital Transformation',
    'Market Entry Strategy',
    'Other'
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 9217468906',
      link: 'tel:+919217468906',
      color: 'bg-yellow-100 text-yellow-700'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'Hawkinsandbrooks@gmail.com',
      link: 'mailto:Hawkinsandbrooks@gmail.com',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      icon: MapPin,
      title: 'Head Office',
      content: 'Blackthorn Chambers, Suite# 18, 18th Floor, Gold Tower, Wave One, Sector 18, Noida',
      link: 'https://maps.google.com/?q=Gold+Tower+Wave+One+Sector+18+Noida',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon-Sat: 9:30 AM - 6:30 PM',
      subtitle: 'Sunday: Closed',
      link: null,
      color: 'bg-orange-100 text-orange-700'
    }
  ];

  // Custom WhatsApp Icon Component
  const WhatsAppIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-5 h-5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </svg>
  );

  return (
    <section className="bg-white min-h-screen">
      {/* Hero Section with Background */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Let's Connect
          </h1>
          <p className="text-lg md:text-xl text-yellow-50 max-w-3xl mx-auto">
            Have a question or ready to start your journey? We're here to help transform your business vision into reality
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        {/* Main Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column - Info & Map (2/5 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Why Choose Us */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3
                className="text-2xl font-bold mb-6 text-black border-b-2 border-yellow-600 pb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Why Choose Us?
              </h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <div className="bg-yellow-100 rounded-full p-1 mr-3 mt-0.5">
                    <span className="text-yellow-600 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">Industry Expertise</h4>
                    <p className="text-sm text-gray-600">25+ years of experience</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-yellow-100 rounded-full p-1 mr-3 mt-0.5">
                    <span className="text-yellow-600 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">Global Presence</h4>
                    <p className="text-sm text-gray-600">Operating across 4 continents</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-yellow-100 rounded-full p-1 mr-3 mt-0.5">
                    <span className="text-yellow-600 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">Client Satisfaction</h4>
                    <p className="text-sm text-gray-600">100% retention rate</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-yellow-100 rounded-full p-1 mr-3 mt-0.5">
                    <span className="text-yellow-600 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">Quick Response</h4>
                    <p className="text-sm text-gray-600">24-hour guarantee</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Quick Actions - WhatsApp with Custom Icon */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3
                className="text-xl font-bold mb-4 text-black"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Quick Contact
              </h3>
              <div className="space-y-3">
                <button
                  onClick={handleWhatsApp}
                  className="w-full flex items-center justify-center bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-4 py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg border-2 border-white"
                >
                  <WhatsAppIcon />
                  <span className="ml-2">Chat on WhatsApp</span>
                </button>
                <a
                  href="tel:+919217468906"
                  className="w-full flex items-center justify-center bg-white hover:bg-gray-50 text-black border-2 border-black font-semibold px-4 py-3 rounded-lg transition-all duration-300"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </div>
            </div>

            {/* Map - Compact */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="p-4">
                <h3 className="font-bold text-black flex items-center">
                  <MapPin className="w-5 h-5 text-yellow-600 mr-2" />
                  Visit Our Office
                </h3>
              </div>
              <div className="relative w-full h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.5728846743837!2d77.31878931508043!3d28.569483982423044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5a53e56dd2b%3A0x83070189899e20a5!2sWave%20Mall%2C%20Sector%2018%2C%20Noida!5e0!3m2!1sen!2sin!4v1736058000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>
              <div className="p-4">
                <a
                  href="https://maps.google.com/?q=Gold+Tower+Wave+One+Sector+18+Noida"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-600 hover:text-yellow-700 font-semibold text-sm flex items-center justify-center"
                >
                  <Navigation className="w-4 h-4 mr-1" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Form (3/5 width) */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 rounded-xl p-6 md:p-8">
              <div className="flex items-center mb-6">
                <Send className="w-8 h-8 text-yellow-600 mr-3" />
                <h2
                  className="text-3xl font-bold text-black"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Send Your Inquiry
                </h2>
              </div>

              <p className="text-gray-600 mb-6">
                Fill out the form below and our team will get back to you within 24 hours
              </p>

              {/* Status Message */}
              {submitStatus.message && (
                <div
                  className={`mb-6 p-4 rounded-lg text-sm ${
                    submitStatus.type === 'success'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold mb-2 text-black">
                    Full Name <span className="text-yellow-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black placeholder-gray-400 transition-all"
                    placeholder="Your Name"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="mobile" className="block text-sm font-bold mb-2 text-black">
                      Mobile Number <span className="text-yellow-600">*</span>
                    </label>
                    <input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black placeholder-gray-400 transition-all"
                      placeholder="Your Number"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold mb-2 text-black">
                      Email <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black placeholder-gray-400 transition-all"
                      placeholder="your@example.com"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="advisoryType" className="block text-sm font-bold mb-2 text-black">
                      Advisory Type <span className="text-yellow-600">*</span>
                    </label>
                    <select
                      id="advisoryType"
                      name="advisoryType"
                      value={formData.advisoryType}
                      onChange={handleChange}
                      className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black transition-all"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select type</option>
                      {advisoryTypes.map((type, index) => (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-bold mb-2 text-black">
                      Subject <span className="text-yellow-600">*</span>
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black placeholder-gray-400 transition-all"
                      placeholder="Brief subject"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold mb-2 text-black">
                    Your Message <span className="text-yellow-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-black placeholder-gray-400 transition-all resize-none"
                    placeholder="Tell us about your needs and how we can help..."
                    rows="5"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <button
                  type="submit"
                  className={`w-full ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  } text-white font-bold px-6 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>

              {/* Contact Info Cards - Horizontal Below Submit Button */}
              <div className="mt-8 pt-8 border-t-2 border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {contactInfo.map((item, index) => (
                    <div
                      key={index}
                      onClick={item.title === 'Head Office' ? handleOpenMap : undefined}
                      className={`bg-white hover:bg-gray-50 rounded-lg p-4 transition-all duration-300 text-center ${
                        item.title === 'Head Office' ? 'cursor-pointer hover:shadow-md' : ''
                      }`}
                    >
                      <div className={`inline-flex items-center justify-center ${item.color} w-10 h-10 rounded-full mb-3`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <h4 className="font-bold text-black mb-1 text-sm">{item.title}</h4>
                      {item.link && item.title !== 'Head Office' ? (
                        <a
                          href={item.link}
                          target={item.link.startsWith('http') ? '_blank' : undefined}
                          rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-gray-600 hover:text-yellow-600 transition-colors text-xs break-words block"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <>
                          <p className="text-gray-600 text-xs">{item.content}</p>
                          {item.subtitle && (
                            <p className="text-gray-600 text-xs mt-1">{item.subtitle}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
